import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Modal } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Fulfillment } from 'api/models/Fulfillment';
import { ApiService } from 'api/services/ApiService';
import { AttioQueryKeys } from 'common/constants/analyst-dashboards';
import { useEventBus } from 'context/EventBusContext/EventBusContext';
import { EventType } from 'context/EventBusContext/EventBusContext.type';
import { useThemeMode } from 'context/ThemeModeContext';
import BrowseDocumentsModal from 'dashboard409a/components/BrowseDocumentsModal';
import OnboardingEmailModal from 'dashboard409a/components/OnboardingEmailModal';
import ProcessReportModal from 'dashboard409a/components/ProcessReportModal';
import type { EngagementManagerOption } from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/ValuationsTableRow.types';
import { useAttioValuationContext } from 'dashboard409a/context/AttioValuationContext';
import { useCompanyMeasurementDateByAttioValuationId } from 'dashboard409a/hooks/useCompanyMeasurementDateByAttioValuationId';
import {
  EmptyOperationsMessage,
  ModelStatus,
  ValuedEntity,
  ValuedInformation,
} from 'dashboard409a/pages/Operations/components';
import { modelStatusMap } from 'dashboard409a/pages/Operations/components/ModelStatus/ModelStatus.constants';
import { NotesDisplay } from 'dashboard409a/pages/Operations/components/NotesDisplay';
import { ModelStatus as ModelStatusEnum } from 'dashboard409a/pages/Workboard/common/types';
import { sendFinalInvoice, updateValuationDealStage } from 'dashboard409a/services/attio';
import { AttioValuationFormattedResponse } from 'dashboard409a/services/attio.types';
import { FulfillmentQueryKeys, useFulfillmentList } from 'dashboard409a/services/fulfillment';
import type { AttioValuationDetailResponse } from 'dashboard409a/services/shared-types';
import { buildEnsureFulfillmentBaseRequest, resolveCompanyIdForFolderSync } from 'dashboard409a/utilities/ensureFulfillmentFromValuationDetail';
import {
  runValuationStatusTransition,
} from 'dashboard409a/utilities/ensureValuationStatusChange';
import lightTheme, { darkTheme } from 'theme';
import { Logger } from 'utilities';
import { DROPBOX_ROOT_OPTIONS } from './dropbox.constants';
import { normalizeFolderSegment } from './dropbox.utils';
import { BentoGrid, CloseButtonContainer, StyledModalBox } from './OperationsModal.styles';
import { OperationsModalProps, StatusRollbackSnapshot } from './OperationsModal.types';
import useDropboxFolderSync from './useDropboxFolderSync';
import { DROPBOX_FOLDER_SYNC_MESSAGES } from './useDropboxFolderSync.constants';
import { addBusinessDays, useValuationDetail } from './useOperationsModal';
import { getValuationFolderNamePrefix } from './valuationTypeFolderPrefix.constants';
import { resolveValuationFolderPath } from './workboardPath.utils';

const OperationsModal: FC<OperationsModalProps> = ({ open, onClose, data, isFromModels = false }) => {
  const { resolvedMode } = useThemeMode();
  const activeTheme = resolvedMode === 'dark' ? darkTheme : lightTheme;
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { publish } = useEventBus();
  const { setCurrentAttioCompanyName, setCurrentAttioValuation } = useAttioValuationContext();
  const [optimisticStatus, setOptimisticStatus] = useState<{ statusId: string; label: string } | null>(null);

  // Use the custom hook to handle valuation detail transformation and API calls
  const {
    valuationDetail,
    valuationId,
    isLoading,
    isError,
    error,
    refetchValuationDetail,
    statusOptions,
    isLoadingStatusOptions,
  } = useValuationDetail({ data, open });

  // Fetch company measurement date by attio_valuation_id - this includes the full company data
  const {
    data: companyMeasurementDateByAttioId,
    error: companyMeasurementDateError,
    isLoading: isLoadingCompanyMeasurementDate,
  } = useCompanyMeasurementDateByAttioValuationId(valuationId);

  // Extract company from companyMeasurementDateByAttioId
  const currentCompany = companyMeasurementDateByAttioId?.company;

  // Use the App company ID for fulfillment operations (not the Attio valued entity ID)
  // The fulfillment API expects the app company ID from companyMeasurementDateByAttioId
  const appCompanyId = useMemo(() => {
    if (currentCompany?.id) {
      return currentCompany.id.toString();
    }
    return undefined;
  }, [currentCompany?.id]);

  // Fetch fulfillment data to check if model support exists
  const { data: fulfillmentData, refetch: refetchFulfillments } = useFulfillmentList(appCompanyId);

  // Find existing fulfillment for the current company measurement date
  const existingFulfillment = useMemo(() => {
    if (!fulfillmentData?.results || !companyMeasurementDateByAttioId?.id) {
      return null;
    }
    return (
      fulfillmentData.results.find(
        fulfillment => fulfillment.company_measurement_date === companyMeasurementDateByAttioId.id
      ) || null
    );
  }, [fulfillmentData, companyMeasurementDateByAttioId?.id]);

  const [isEnsureModelSupportPending, setIsEnsureModelSupportPending] = useState(false);
  const [isResolvingCompanyId, setIsResolvingCompanyId] = useState(false);

  const suggestedCompanyFolderName = useMemo(
    () => normalizeFolderSegment(valuationDetail?.valuedEntity?.companyName || ''),
    [valuationDetail?.valuedEntity?.companyName]
  );

  const suggestedValuationFolderName = useMemo(() => {
    const rawDate = valuationDetail?.valuedInformation?.valuationDate || valuationDetail?.deal?.valuationDate;
    if (!rawDate) {
      return '';
    }
    const parsed = new Date(rawDate);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    const year = parsed.getFullYear();
    const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
    const datePart = `${year}.${month}`;
    const prefix = getValuationFolderNamePrefix(valuationDetail?.valuedInformation?.valuationType);
    return prefix ? `${prefix} ${datePart}` : datePart;
  }, [
    valuationDetail?.valuedInformation?.valuationDate,
    valuationDetail?.valuedInformation?.valuationType,
    valuationDetail?.deal?.valuationDate,
  ]);

  const {
    dropboxRoot,
    setDropboxRoot,
    companyFolderStatus,
    valuationFolderStatus,
    companyFolderMatchPaths,
    valuationFolderMatchPaths,
    companyFolderSimilarMatchesCount,
    valuationFolderSimilarMatchesCount,
    handleUpdateCompanyFolder: syncCompanyFolder,
    handleUpdateValuationFolder,
  } = useDropboxFolderSync({
    open,
    valuationId,
    currentCompany,
    companyMeasurementDateById: companyMeasurementDateByAttioId,
    isLoadingMeasurementDate: isLoadingCompanyMeasurementDate,
    measurementDateError: companyMeasurementDateError,
    suggestedCompanyFolderName,
    suggestedValuationFolderName,
    queryClient,
  });

  const isCompanyFolderSaveDisabled = isLoadingCompanyMeasurementDate || isResolvingCompanyId;

  const handleUpdateCompanyFolder = useCallback(
    async (folderName: string, selectedFolderPath?: string | null) => {
      if (!valuationId) {
        return;
      }

      setIsResolvingCompanyId(true);
      try {
        const resolveResult = await resolveCompanyIdForFolderSync({
          queryClient,
          valuationId,
          knownCompanyId: currentCompany?.id,
          isCmdQueryLoading: isLoadingCompanyMeasurementDate,
          valuationDetail,
          modalData: data,
        });

        if (resolveResult.status === 'loading') {
          enqueueSnackbar(DROPBOX_FOLDER_SYNC_MESSAGES.LOADING_COMPANY_LINK, { variant: 'info' });
          return;
        }

        if (resolveResult.status === 'failed') {
          const message =
            resolveResult.reason === 'missing_valuation_date'
              ? DROPBOX_FOLDER_SYNC_MESSAGES.MISSING_VALUATION_DATE_FOR_COMPANY_LINK
              : DROPBOX_FOLDER_SYNC_MESSAGES.COMPANY_LINK_FAILED;
          enqueueSnackbar(message, { variant: 'error', autoHideDuration: 10000 });
          return;
        }

        await syncCompanyFolder(folderName, selectedFolderPath, resolveResult.companyId);
      } finally {
        setIsResolvingCompanyId(false);
      }
    },
    [
      valuationId,
      queryClient,
      currentCompany?.id,
      isLoadingCompanyMeasurementDate,
      valuationDetail,
      data,
      enqueueSnackbar,
      syncCompanyFolder,
    ]
  );

  const isBrowseDocumentsDisabled =
    !currentCompany?.id || !companyMeasurementDateByAttioId?.measurement_date?.id;

  const [isBrowseDocumentsOpen, setIsBrowseDocumentsOpen] = useState(false);
  const [isProcessReportOpen, setIsProcessReportOpen] = useState(false);
  const [isOnboardingEmailOpen, setIsOnboardingEmailOpen] = useState(false);
  const [isOnboardingEmailPending, setIsOnboardingEmailPending] = useState(false);

  const informationPageUrl = useMemo(() => {
    if (!currentCompany?.slug || !companyMeasurementDateByAttioId?.measurement_date?.slug) {
      return null;
    }

    return `/dashboard/companies/${currentCompany.slug}/information?md=${companyMeasurementDateByAttioId.measurement_date.slug}`;
  }, [companyMeasurementDateByAttioId?.measurement_date?.slug, currentCompany?.slug]);

  // Resolve the absolute valuation folder path (parent of Model/ and Report/).
  const valuationFolderPath = useMemo(
    () =>
      resolveValuationFolderPath(
        currentCompany?.file_storage_reference,
        companyMeasurementDateByAttioId?.file_storage_reference,
        dropboxRoot
      ),
    [
      companyMeasurementDateByAttioId?.file_storage_reference,
      currentCompany?.file_storage_reference,
      dropboxRoot,
    ]
  );

  const handleProcessReport = useCallback(() => {
    setIsProcessReportOpen(true);
  }, []);

  const handleOnboardingEmail = useCallback(async () => {
    if (!valuationId || !valuationDetail) {
      enqueueSnackbar('Valuation details are not loaded yet. Please wait and try again.', {
        variant: 'error',
      });
      return;
    }

    if (companyMeasurementDateByAttioId?.id) {
      setIsOnboardingEmailOpen(true);
      return;
    }

    const ensureBase = buildEnsureFulfillmentBaseRequest({
      valuationDetail,
      valuationId,
      modalData: data,
    });
    if (!ensureBase) {
      enqueueSnackbar(
        'Valuation date is missing or invalid. Cannot create measurement date. Please fix the valuation in Attio.',
        { variant: 'error', autoHideDuration: 10000 }
      );
      return;
    }

    setIsOnboardingEmailPending(true);
    try {
      await ApiService.apiDashboardOperationsEnsureFulfillmentForAttioValuationCreate(ensureBase);
      await queryClient.invalidateQueries({
        queryKey: ['company-measurement-date-by-attio-valuation-id', valuationId],
      });
      await queryClient.refetchQueries({
        queryKey: ['company-measurement-date-by-attio-valuation-id', valuationId],
      });
      setIsOnboardingEmailOpen(true);
    } catch (error) {
      Logger.error('Failed to ensure measurement date for onboarding email:', error);
      enqueueSnackbar('Failed to create measurement date. Please try again.', { variant: 'error' });
    } finally {
      setIsOnboardingEmailPending(false);
    }
  }, [
    valuationId,
    valuationDetail,
    companyMeasurementDateByAttioId?.id,
    data,
    enqueueSnackbar,
    queryClient,
  ]);

  useEffect(() => {
    if (!open) {
      setIsProcessReportOpen(false);
      setIsOnboardingEmailOpen(false);
    }
  }, [open]);

  const applyOptimisticStatusDisplay = useCallback(
    (statusId: string) => {
      if (!valuationId) {
        return;
      }
      const selectedStatus = statusOptions.find(option => option.id === statusId);
      const label = selectedStatus?.label || 'status';
      setOptimisticStatus({ statusId, label });
      queryClient.setQueryData(
        [AttioQueryKeys.VALUATION_ITEM, valuationId],
        (old: AttioValuationDetailResponse | undefined) => (old ? { ...old, statusId, status: label } : old)
      );
      publish(EventType.WORKBOARD_VALUATION_UPDATED, {
        valuationId,
        updates: { status: label },
      });
    },
    [valuationId, statusOptions, queryClient, publish]
  );

  const captureStatusRollbackSnapshot = useCallback((): StatusRollbackSnapshot => {
    const queryData = valuationId ? queryClient.getQueryData([AttioQueryKeys.VALUATION_ITEM, valuationId]) : undefined;
    return {
      queryData,
      optimisticStatus,
      workboardStatusLabel: optimisticStatus?.label ?? valuationDetail?.status,
    };
  }, [valuationId, queryClient, optimisticStatus, valuationDetail?.status]);

  const restoreStatusRollbackSnapshot = useCallback(
    (snapshot: StatusRollbackSnapshot) => {
      if (valuationId) {
        const queryKey = [AttioQueryKeys.VALUATION_ITEM, valuationId];
        if (snapshot.queryData !== undefined) {
          queryClient.setQueryData(queryKey, snapshot.queryData);
        }
      }
      setOptimisticStatus(snapshot.optimisticStatus);
      if (valuationId && snapshot.workboardStatusLabel !== undefined) {
        publish(EventType.WORKBOARD_VALUATION_UPDATED, {
          valuationId,
          updates: { status: snapshot.workboardStatusLabel },
        });
      }
    },
    [valuationId, queryClient, publish]
  );

  const persistStatusChange = useCallback(
    async (statusId: string, snapshot: StatusRollbackSnapshot) => {
      if (!valuationId) {
        return;
      }
      try {
        if (!valuationDetail) {
          restoreStatusRollbackSnapshot(snapshot);
          enqueueSnackbar('Valuation details are not loaded yet. Please wait and try again.', {
            variant: 'error',
          });
          return;
        }

        const selectedStatus = statusOptions.find(option => option.id === statusId);
        const statusLabel = selectedStatus?.label || 'status';

        const transitionResult = await runValuationStatusTransition({
          valuationId,
          statusId,
          statusLabel,
          queryClient,
          modalData: data,
          fulfillmentContext: {
            valuationDetail,
            existingFulfillment,
            companyMeasurementDateId: companyMeasurementDateByAttioId?.id,
            appCompanyId,
          },
          companyName: currentCompany?.name || valuationDetail?.valuedEntity?.companyName,
        });
        if (!transitionResult.ok) {
          restoreStatusRollbackSnapshot(snapshot);

          if (transitionResult.reason === 'missing_valuation_date') {
            enqueueSnackbar(
              'Valuation date is missing or invalid. Cannot sync fulfillment. Please fix the valuation in Attio.',
              { variant: 'error', autoHideDuration: 10000 }
            );
          } else if (transitionResult.reason === 'ensure_failed' || transitionResult.reason === 'load_failed') {
            enqueueSnackbar(
              'Failed to sync fulfillment. Status was not updated. Please try again or contact support.',
              { variant: 'error' }
            );
          } else {
            enqueueSnackbar('Failed to update status. Please try again or contact support.', { variant: 'error' });
          }
          return;
        }

        await refetchFulfillments();

        publish(EventType.WORKBOARD_VALUATION_UPDATED, {
          valuationId,
          updates: { status: statusLabel },
        });
        setOptimisticStatus(null);
      } catch (statusError) {
        Logger.error('Error updating valuation status:', statusError);
        restoreStatusRollbackSnapshot(snapshot);
        enqueueSnackbar('Failed to update status. Please try again or contact support.', { variant: 'error' });
      }
    },
    [
      valuationId,
      valuationDetail,
      data,
      statusOptions,
      existingFulfillment,
      companyMeasurementDateByAttioId?.id,
      appCompanyId,
      queryClient,
      publish,
      refetchFulfillments,
      enqueueSnackbar,
      restoreStatusRollbackSnapshot,
      currentCompany?.name,
    ]
  );

  const handleBrowseDocuments = useCallback(() => {
    if (!currentCompany?.id || !companyMeasurementDateByAttioId?.measurement_date?.id) {
      return;
    }

    setIsBrowseDocumentsOpen(true);
  }, [companyMeasurementDateByAttioId?.measurement_date?.id, currentCompany?.id]);

  useEffect(() => {
    if (valuationDetail) {
      setCurrentAttioValuation(valuationDetail as unknown as AttioValuationFormattedResponse);
      if (valuationDetail?.valuedEntity.companyName) {
        setCurrentAttioCompanyName(valuationDetail?.valuedEntity.companyName);
      }
    }
  }, [valuationDetail, setCurrentAttioCompanyName, setCurrentAttioValuation]);

  useEffect(() => {
    if (!open) {
      setOptimisticStatus(null);
    }
  }, [open]);

  if (isError) {
    Logger.error('Error fetching Attio valuations:', error);
  }

  const handleStatusChange = async (statusId: string) => {
    if (!valuationId) {
      return;
    }
    const effectiveCurrentStatusId = optimisticStatus?.statusId ?? valuationDetail?.statusId;
    if (effectiveCurrentStatusId === statusId) {
      return;
    }

    const snapshot = captureStatusRollbackSnapshot();
    applyOptimisticStatusDisplay(statusId);
    enqueueSnackbar('Valuation status has been updated', { variant: 'success' });
    persistStatusChange(statusId, snapshot).catch(() => {});
  };

  const handleDealStageChange = async (dealStageId: string) => {
    if (!dealStageId || !valuationDetail?.deal?.dealId) {
      return;
    }

    try {
      const { dealId } = valuationDetail.deal;
      const companyName = currentCompany?.name || valuationDetail?.valuedEntity?.companyName;
      await updateValuationDealStage(dealId, dealStageId, companyName);

      // Publish event to update the table row immediately
      publish(EventType.WORKBOARD_VALUATION_UPDATED, {
        valuationId,
        updates: {
          dealStage: dealStageId,
        },
      });

      // Invalidate all relevant caches
      await Promise.all([
        // Invalidate the valuation detail query
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.VALUATION_ITEM, valuationId],
          exact: false,
        }),
        // Invalidate the valuations list (for table refresh)
        queryClient.invalidateQueries({
          queryKey: ['valuations-all'],
          exact: false,
        }),
        // Invalidate current/past valuations
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.CURRENT_VALUATIONS],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.PAST_VALUATIONS],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [AttioQueryKeys.VALUATIONS],
          exact: false,
        }),
      ]);

      // Manually refetch the valuation detail to update the modal
      await refetchValuationDetail();

      enqueueSnackbar('Deal stage has been updated', { variant: 'success' });
    } catch (dealStageError) {
      Logger.error('Error updating deal stage:', dealStageError);
      enqueueSnackbar('Failed to update deal stage. Please try again.', { variant: 'error' });
    }
  };

  const handleSendFinalInvoice = async () => {
    const companyName = currentCompany?.name || valuationDetail?.valuedEntity?.companyName;
    await sendFinalInvoice(valuationDetail?.deal?.dealId as string, companyName);

    enqueueSnackbar('Final invoice has been sent', { variant: 'success' });
  };

  // Handle model support creation (ensure graph + fulfillment when CMD was missing)
  const handleCreateModelSupport = async () => {
    if (!valuationId || !valuationDetail) {
      enqueueSnackbar('Valuation details are not loaded yet. Please wait and try again.', {
        variant: 'error',
      });
      return;
    }

    const ensureBase = buildEnsureFulfillmentBaseRequest({
      valuationDetail,
      valuationId,
      modalData: data,
    });
    if (!ensureBase) {
      enqueueSnackbar(
        'Valuation date is missing or invalid. Cannot create model support. Please fix the valuation in Attio.',
        { variant: 'error', autoHideDuration: 10000 }
      );
      return;
    }

    setIsEnsureModelSupportPending(true);
    try {
      await ApiService.apiDashboardOperationsEnsureFulfillmentForAttioValuationCreate({
        ...ensureBase,
        model_status: modelStatusMap[ModelStatusEnum.DRAFT],
        supporting_due_date: addBusinessDays(new Date(), 4),
        reviewers: [],
        preparers: [],
      });

      await queryClient.invalidateQueries({
        queryKey: ['company-measurement-date-by-attio-valuation-id', valuationId],
      });
      await queryClient.invalidateQueries({
        queryKey: [FulfillmentQueryKeys.FULFILLMENT_LIST],
        exact: false,
      });
      await refetchFulfillments();
      enqueueSnackbar('Model support created successfully', { variant: 'success' });
    } catch (fulfillmentError) {
      Logger.error('Failed to create model support:', fulfillmentError);
      enqueueSnackbar('Failed to create model support. Please try again.', { variant: 'error' });
    } finally {
      setIsEnsureModelSupportPending(false);
    }
  };

  const handleModelTeamChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!existingFulfillment || !appCompanyId || !valuationId) {
      enqueueSnackbar('Unable to update model team: Company information not available', {
        variant: 'error',
      });
      return;
    }
    try {
      await ApiService.apiDashboardCompanyOperationsFulfillmentPartialUpdate(appCompanyId, valuationId, {
        id: existingFulfillment.id,
        is_slc_model: event.target.checked,
      } as Fulfillment);
      refetchFulfillments();
    } catch (error_) {
      Logger.error('Error updating model team:', error_);
      enqueueSnackbar('Failed to update model team. Please try again.', { variant: 'error' });
    }
  };

  const handleEngagementManagerUpdate = useCallback(
    async (_recordId: string, _option: EngagementManagerOption) => {
      await queryClient.invalidateQueries({
        queryKey: [AttioQueryKeys.VALUATION_ITEM, valuationId],
        exact: false,
      });
      await refetchValuationDetail();
    },
    [valuationId, queryClient, refetchValuationDetail]
  );

  const handleOtherConsultantsUpdate = useCallback(
    async (_recordId: string, _options: { targetRecordId: string; name: string; imageSrc: string }[]) => {
      await queryClient.invalidateQueries({
        queryKey: [AttioQueryKeys.VALUATION_ITEM, valuationId],
        exact: false,
      });
      await refetchValuationDetail();
    },
    [valuationId, queryClient, refetchValuationDetail]
  );

  const combinedIsLoading = isLoading;

  const valuedEntityBox = (
    <Box>
      <ValuedEntity
        valuedEntity={valuationDetail?.valuedEntity}
        isLoading={combinedIsLoading}
        companyFolder={currentCompany?.file_storage_reference || null}
        dropboxRoot={dropboxRoot}
        dropboxRootOptions={DROPBOX_ROOT_OPTIONS}
        onDropboxRootChange={setDropboxRoot}
        companyFolderStatus={companyFolderStatus}
        companyFolderMatchPaths={companyFolderMatchPaths}
        companyFolderSimilarMatchesCount={companyFolderSimilarMatchesCount}
        suggestedCompanyFolderName={suggestedCompanyFolderName}
        isCompanyFolderSaveDisabled={isCompanyFolderSaveDisabled}
        onUpdateCompanyFolder={handleUpdateCompanyFolder}
      />
    </Box>
  );

  const modelStatusBox = (
    <Box>
      <ModelStatus
        fulfillmentData={fulfillmentData}
        isLoading={combinedIsLoading}
        onCreateModelSupport={handleCreateModelSupport}
        isCreatingModelSupport={isEnsureModelSupportPending}
        companyId={appCompanyId}
        valuationId={valuationId}
        companyMeasurementDateId={companyMeasurementDateByAttioId?.id}
        isSlcModel={existingFulfillment?.is_slc_model || false}
        onSlcModelChange={handleModelTeamChange}
      />
    </Box>
  );

  const notesBox = (
    <Box>
      <NotesDisplay isFromModels={isFromModels} />
    </Box>
  );

  const valuedInformationFullWidth = (
    <Box sx={{ gridColumn: '1 / -1' }}>
      <ValuedInformation
        isFromModels={isFromModels}
        valuedInformation={valuationDetail?.valuedInformation}
        deal={valuationDetail?.deal}
        onDealStageChange={handleDealStageChange}
        onSendFinalInvoice={handleSendFinalInvoice}
        isLoading={combinedIsLoading}
        statusOptions={statusOptions}
        currentStatusId={optimisticStatus?.statusId ?? valuationDetail?.statusId}
        onStatusChange={handleStatusChange}
        isStatusLoading={combinedIsLoading || isLoadingStatusOptions}
        companyName={currentCompany?.name || valuationDetail?.valuedEntity?.companyName || null}
        companyFolder={currentCompany?.file_storage_reference || null}
        valuationFolder={companyMeasurementDateByAttioId?.file_storage_reference || null}
        dropboxRoot={dropboxRoot}
        valuationFolderStatus={valuationFolderStatus}
        valuationFolderMatchPaths={valuationFolderMatchPaths}
        valuationFolderSimilarMatchesCount={valuationFolderSimilarMatchesCount}
        suggestedValuationFolderName={suggestedValuationFolderName}
        onUpdateValuationFolder={handleUpdateValuationFolder}
        onEngagementManagerUpdate={handleEngagementManagerUpdate}
        onOtherConsultantsUpdate={handleOtherConsultantsUpdate}
        onBrowseDocuments={handleBrowseDocuments}
        isBrowseDocumentsDisabled={isBrowseDocumentsDisabled}
        onProcessReport={handleProcessReport}
        isProcessReportDisabled={false}
        onOnboardingEmail={handleOnboardingEmail}
        isOnboardingEmailDisabled={isOnboardingEmailPending}
      />
    </Box>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableEnforceFocus
      disableAutoFocus
      sx={{
        '&:focus': {
          outline: 'none',
        },
        '&:focus-visible': {
          outline: 'none',
        },
      }}>
      <ThemeProvider theme={activeTheme}>
        <StyledModalBox onClick={e => e.stopPropagation()}>
          <CloseButtonContainer>
            <IconButton onClick={onClose} size="small" aria-label="close" sx={{ padding: '4px' }}>
              <CloseIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          </CloseButtonContainer>
          {valuationId ? (
            <BentoGrid>
              {isFromModels ? (
                <>
                  {valuedEntityBox}
                  {modelStatusBox}
                  {notesBox}
                  {valuedInformationFullWidth}
                </>
              ) : (
                <>
                  {valuedInformationFullWidth}
                  {valuedEntityBox}
                  {modelStatusBox}
                  {notesBox}
                </>
              )}
            </BentoGrid>
          ) : (
            <EmptyOperationsMessage />
          )}
          <BrowseDocumentsModal
            open={isBrowseDocumentsOpen}
            onClose={() => setIsBrowseDocumentsOpen(false)}
            companyId={currentCompany?.id}
            companyName={currentCompany?.name || valuationDetail?.valuedEntity?.companyName || null}
            firmId={currentCompany?.firm}
            measurementDateId={companyMeasurementDateByAttioId?.measurement_date?.id}
            measurementDateLabel={
              companyMeasurementDateByAttioId?.measurement_date?.name
              || companyMeasurementDateByAttioId?.measurement_date?.date
              || null
            }
            informationPageUrl={informationPageUrl}
          />
          <ProcessReportModal
            open={isProcessReportOpen}
            onClose={() => setIsProcessReportOpen(false)}
            cmdId={companyMeasurementDateByAttioId?.id}
            valuationId={valuationId}
            companyName={currentCompany?.name || valuationDetail?.valuedEntity?.companyName || null}
            valuationFolderPath={valuationFolderPath}
          />
          <OnboardingEmailModal
            open={isOnboardingEmailOpen}
            onClose={() => setIsOnboardingEmailOpen(false)}
            cmdId={companyMeasurementDateByAttioId?.id}
            measurementDateId={companyMeasurementDateByAttioId?.measurement_date?.id}
            companyId={currentCompany?.id}
            companyName={currentCompany?.name || valuationDetail?.valuedEntity?.companyName || null}
            valuationId={valuationId}
          />
        </StyledModalBox>
      </ThemeProvider>
    </Modal>
  );
};

export default OperationsModal;
